const VALID_CONFIDENCE = new Set(['exact', 'inferred', 'manual', 'unknown']);

export function normalizeMarketingConfidence(value, fallback = 'unknown') {
  const key = String(value || '').toLowerCase().trim();
  return VALID_CONFIDENCE.has(key) ? key : fallback;
}

export function hasMarketingAttribution(row = {}) {
  return Boolean(row?.marketing_source_id || row?.marketing_campaign_id);
}

export function marketingAttributionFromClient(cliente = {}, { fallbackConfidence = 'unknown', notes = 'Heredado desde origen de adquisicion del cliente.' } = {}) {
  const sourceId = cliente?.acquisition_source_id || null;
  const campaignId = cliente?.acquisition_campaign_id || null;
  const hasAttribution = Boolean(sourceId || campaignId);
  if (!hasAttribution) return {};
  return {
    marketing_source_id: sourceId || null,
    marketing_campaign_id: campaignId || null,
    marketing_attribution_confidence: normalizeMarketingConfidence(cliente?.acquisition_confidence, fallbackConfidence),
    marketing_attribution_notes: notes,
  };
}

export function marketingAttributionFromRecord(row = {}, { fallbackConfidence = 'unknown', notes = 'Heredado desde registro vinculado.' } = {}) {
  const sourceId = row?.marketing_source_id || null;
  const campaignId = row?.marketing_campaign_id || null;
  const hasAttribution = Boolean(sourceId || campaignId);
  if (!hasAttribution) return {};
  return {
    marketing_source_id: sourceId || null,
    marketing_campaign_id: campaignId || null,
    marketing_attribution_confidence: normalizeMarketingConfidence(row?.marketing_attribution_confidence, fallbackConfidence),
    marketing_attribution_notes: row?.marketing_attribution_notes || notes,
  };
}

export function mergeMissingMarketingAttribution(target = {}, inherited = {}) {
  const patch = {};
  if (!target.marketing_source_id && inherited.marketing_source_id) patch.marketing_source_id = inherited.marketing_source_id;
  if (!target.marketing_campaign_id && inherited.marketing_campaign_id) patch.marketing_campaign_id = inherited.marketing_campaign_id;

  const hasAny = Boolean(
    target.marketing_source_id ||
    target.marketing_campaign_id ||
    patch.marketing_source_id ||
    patch.marketing_campaign_id,
  );

  if (hasAny && !target.marketing_attribution_confidence && inherited.marketing_attribution_confidence) {
    patch.marketing_attribution_confidence = normalizeMarketingConfidence(inherited.marketing_attribution_confidence, 'inferred');
  }
  if (hasAny && !target.marketing_attribution_notes && inherited.marketing_attribution_notes) {
    patch.marketing_attribution_notes = inherited.marketing_attribution_notes;
  }
  return patch;
}

export function inheritedMarketingAttribution({ quote, cobro, cliente } = {}) {
  if (hasMarketingAttribution(quote)) {
    return marketingAttributionFromRecord(quote, {
      fallbackConfidence: 'inferred',
      notes: 'Heredado desde cotizacion vinculada.',
    });
  }
  if (hasMarketingAttribution(cobro)) {
    return marketingAttributionFromRecord(cobro, {
      fallbackConfidence: 'inferred',
      notes: 'Heredado desde cobro vinculado.',
    });
  }
  if (cliente) {
    return marketingAttributionFromClient(cliente, {
      fallbackConfidence: 'inferred',
      notes: 'Inferido desde origen de adquisicion del cliente.',
    });
  }
  return {};
}

export function marketingConfidenceMeta(value) {
  const key = normalizeMarketingConfidence(value, 'unknown');
  if (key === 'exact') return { key, label: 'Exacto', tone: 'green', color: 'var(--green)' };
  if (key === 'manual') return { key, label: 'Manual', tone: 'blue', color: 'var(--blue)' };
  if (key === 'inferred') return { key, label: 'Inferido', tone: 'gold', color: 'var(--gold)' };
  return { key: 'unknown', label: 'Desconocido', tone: 'gray', color: 'var(--text-d)' };
}

export function marketingSourceName(data = {}, id) {
  const source = (data.marketingSources || []).find(row => String(row.id) === String(id || ''));
  return source?.name || '';
}

export function marketingCampaignName(data = {}, id) {
  const campaign = (data.marketingCampaigns || []).find(row => String(row.id) === String(id || ''));
  return campaign?.name || '';
}

export function marketingAttributionDisplay(row = {}, data = {}) {
  const sourceName = marketingSourceName(data, row.marketing_source_id);
  const campaignName = marketingCampaignName(data, row.marketing_campaign_id);
  const confidence = marketingConfidenceMeta(row.marketing_attribution_confidence);
  const hasAny = Boolean(row.marketing_source_id || row.marketing_campaign_id);
  return {
    hasAny,
    sourceName,
    campaignName,
    confidence,
    label: hasAny
      ? [sourceName || 'Fuente sin nombre', campaignName].filter(Boolean).join(' - ')
      : 'Sin atribucion de marketing',
  };
}
